/*
 * Who may read the article (2026-10-26, ENHANCEMENTS S46): the owner's toggle,
 * Public or Private, defaulting to Public; and, while Private, a checklist of the
 * audiences this deployment configures, at least one of which must be ticked.
 *
 * Not a publishing gate. The Publishing card still decides whether the article
 * is out; this decides who reads it once it is - everybody, or the audiences'
 * members and the knowledge base's administrators.
 *
 * The audiences are configuration (VISIBILITY_AUDIENCE_* application settings on
 * the server), carried here by the metadata request as keys and names. Two
 * states are said out loud rather than left to surprise:
 *
 *   - a key the article holds that the server no longer configures. It matches
 *     no reader, the server refuses to save it, and it is not in the checklist -
 *     so it is named here, as going on the next save. The parent leaves it out
 *     of what it sends.
 *   - no audience configured at all. Private cannot be chosen then (there would
 *     be nothing to tick); an article that is ALREADY private keeps the choice,
 *     so its author can see why it cannot be saved and make it public.
 *
 * Controlled: the parent holds the values, because they are saved with the rest
 * of its form.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useId } from 'react';
import type { AdminArticleAudience, ArticleAudienceOption, ArticleVisibility } from '@accotool-kb/shared';
import { useMessages } from '../../i18n/language.js';

export function VisibilityField({
  visibility,
  audienceKeys,
  onChange,
  options,
  unconfigured = [],
  error,
}: {
  visibility: ArticleVisibility;
  audienceKeys: string[];
  onChange: (visibility: ArticleVisibility, audienceKeys: string[]) => void;
  /** The configured audiences, in name order; null while they are loading. */
  options: ArticleAudienceOption[] | null;
  /** Keys the article holds that are no longer configured (see the header). */
  unconfigured?: AdminArticleAudience[];
  error?: string | undefined;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.visibility;
  const id = useId();
  const labelId = `${id}-visibility`;
  const audiencesId = `${id}-audiences`;
  const hintId = `${id}-hint`;

  const noneConfigured = options !== null && options.length === 0;
  const privateDisabled = noneConfigured && visibility !== 'private';

  const toggle = (key: string) =>
    onChange(
      visibility,
      audienceKeys.includes(key) ? audienceKeys.filter((value) => value !== key) : [...audienceKeys, key],
    );

  return (
    <div className={`field${error ? ' field-invalid' : ''}`}>
      <label id={labelId}>{words.label}</label>

      <div className="choice-switch" role="radiogroup" aria-labelledby={labelId} aria-describedby={hintId}>
        {(['public', 'private'] as const).map((value) => (
          <label key={value} className={value === 'private' && privateDisabled ? 'is-disabled' : undefined}>
            <input
              type="radio"
              name={`${id}-visibility-choice`}
              value={value}
              checked={visibility === value}
              disabled={value === 'private' && privateDisabled}
              onChange={() => onChange(value, audienceKeys)}
            />
            <span>{t.admin.rules.visibility(value)}</span>
          </label>
        ))}
      </div>

      <p className="field-hint" id={hintId}>
        {visibility === 'public' ? words.publicHint : words.privateHint}
        {privateDisabled && ` ${words.privateUnavailable}`}
      </p>

      {visibility === 'private' && (
        <>
          <span id={audiencesId} className="field-sublabel">
            {words.audiences}
            <span aria-hidden="true"> *</span>
          </span>
          <div className="tag-picker" role="group" aria-labelledby={audiencesId}>
            {options === null ? (
              <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
                {words.loadingAudiences}
              </p>
            ) : options.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
                {words.noAudiences}
              </p>
            ) : (
              <ul className="checkbox-list">
                {options.map((option) => (
                  <li key={option.key}>
                    <label>
                      <input
                        type="checkbox"
                        name={`${id}-audience`}
                        value={option.key}
                        checked={audienceKeys.includes(option.key)}
                        onChange={() => toggle(option.key)}
                      />
                      <span>{option.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {unconfigured.length > 0 && (
            <p className="field-warning">
              {words.noLongerConfigured(unconfigured.map((audience) => audience.key).join(', '))}
            </p>
          )}
        </>
      )}

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The parts of a stored audience list the editor can keep: the keys this server
 * still configures - which it sends - and the ones it does not, which it names.
 */
export function splitStoredAudiences(
  stored: readonly AdminArticleAudience[],
  options: readonly ArticleAudienceOption[] | null,
): { keep: string[]; unconfigured: AdminArticleAudience[] } {
  if (options === null) {
    // Not loaded yet: trust the server's own verdict on each key.
    return {
      keep: stored.filter((audience) => audience.name !== null).map((audience) => audience.key),
      unconfigured: stored.filter((audience) => audience.name === null),
    };
  }
  const configured = new Set(options.map((option) => option.key));
  return {
    keep: stored.filter((audience) => configured.has(audience.key)).map((audience) => audience.key),
    unconfigured: stored.filter((audience) => !configured.has(audience.key)),
  };
}
